import { DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { createSelector } from '@reduxjs/toolkit';
import pick from 'lodash/fp/pick';
import { useCallback, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { DateRange } from 'dashboard/components-lib/DashboardDatePicker/DashboardDatePicker';
import useTableViews from 'dashboard/lib/hooks/useTableViews';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { TableKey } from 'dashboard/models/table-view';
import { WebhookEventStatus } from 'dashboard/models/webhook-event';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  setCurrentPage,
  setCurrentTableView,
  setFilters,
  setPageSize,
  setTableViews,
} from 'dashboard/redux/slices/webhook-events';
import { RootState } from 'dashboard/redux/store';
import { loadWebhookEventList } from 'dashboard/redux/thunk-actions/webhook-events';
import DashboardWebhookEventsView from 'dashboard/views/DashboardWebhookEventsView';
import useApiLoader from 'lib/hooks/useApiLoader';
import useTable from 'lib/hooks/useTable';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { getDateRange, updateDateFilters } from 'lib/utils/filters';

type DashboardWebhookEventsRouteProps = PropsFromRedux;

const getTableViewState = (state: RootState) => state.webhookEvents.tableViews;
const selectTableViews = createSelector(getTableViewState, (state) => state.views);
const selectCurrentTableView = createSelector([getTableViewState], (tableViews) => {
  return tableViews.views.find((tv) => tv.id === tableViews.currentTableView);
});

const DashboardWebhookEventsRoute = (props: DashboardWebhookEventsRouteProps) => {
  const { currentEnvironment, currentPage, filters, pageSize } = props;

  const { handlePageLinkClick, handlePageSizeClick, handleOmniSearch } = useTable({
    filters: filters,
    pageSize: pageSize,
    currentPage: currentPage,
    setFilters: props.setFilters,
    setCurrentPage: props.setCurrentPage,
    setPageSize: props.setPageSize,
  });

  const handleDateUpdate = useCallback(
    ({ startDate, endDate }: DateRange) => {
      if (startDate || endDate) {
        const { start, end } = getDateRange(startDate, endDate);
        props.setFilters({
          ...filters,
          updatedAtStart: start,
          updatedAtEnd: end,
        });
      } else {
        props.setFilters({ ...filters, updatedAtStart: undefined, updatedAtEnd: undefined });
      }
      props.setCurrentPage(1);
    },
    [props.setFilters, props.setCurrentPage],
  );

  const handleDateUpdateV2 = useCallback(
    (newDateRange: RangeValue<DateValue> | null) => {
      updateDateFilters(newDateRange, handleDateUpdate);
    },
    [handleDateUpdate],
  );

  const handleStatusFilterUpdate = (statuses: WebhookEventStatus[]) => {
    props.setFilters({ status: statuses });
    handlePageLinkClick(1);
  };

  const abortController = useMemo(
    () => new AbortController(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadWebhookEventList, currentEnvironment, currentPage, pageSize, filters],
  );

  const { loading: loadingWebhookEvents } = useApiLoader({
    loader: () =>
      props.loadWebhookEventList({
        currentEnvironment,
        currentPage: currentPage || 1,
        pageSize: pageSize || 50,
        filters: filters || {},
        abortSignal: abortController.signal,
      }),
    deps: [props.loadWebhookEventList, currentEnvironment, currentPage, pageSize, filters],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load Webhook Events.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const tableViewsState = useTableViews({
    tableKey: TableKey.WebhookEventsIndex,
    filters: pick('status', filters),
    onSetTableView: (tableView) => {
      if (!tableView) {
        props.setFilters({
          status: undefined,
        });
        return;
      }

      const tableViewFilters = tableView.attributes.configuration.filters;
      props.setFilters({
        status: tableViewFilters.status,
      });
    },
    setCurrentTableView,
    setTableViews,
    selectTableViews,
    selectCurrentTableView,
  });

  const loading = loadingWebhookEvents || tableViewsState.loadingTableViews;

  return (
    <DashboardWebhookEventsView
      webhookEvents={props.webhookEvents}
      filters={filters}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      paginationParams={props.paginationParams}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      pageSize={pageSize}
      isLoading={loading}
      handlePageLinkClick={handlePageLinkClick}
      handlePageSizeClick={handlePageSizeClick}
      handleDateUpdate={handleDateUpdateV2}
      handleOmniSearch={handleOmniSearch}
      handleStatusFilterUpdate={handleStatusFilterUpdate}
      tableViewsState={tableViewsState}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    webhookEvents: state.webhookEvents.webhookEvents,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    pageSize: state.webhookEvents.pageSize,
    currentPage: state.webhookEvents.currentPage,
    paginationParams: state.webhookEvents.paginationParams,
    filters: state.webhookEvents.filters,
  };
};

const mapDispatchToProps = {
  loadWebhookEventList,
  setFilters,
  setPageSize,
  setCurrentPage,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardWebhookEventsRoute);
